
import { Component, forwardRef, KeyValueDiffers, Inject, OnChanges, ViewChild, OnInit, ElementRef, ChangeDetectorRef } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import * as htmlToImage from 'html-to-image';
import * as papaparse from 'papaparse';
import * as am4core from '@amcharts/amcharts4/core';

import { BlockComponent } from '../../../../../Common/PageBuilder/components/Block/block.component';
import { SeriesModel } from '../../../../../Common/Charts/models/series.model';
import { AbstractPageBuilderService } from '../../../../../Common/PageBuilder/services/abstract-page-builder.service';
import { ChartDisplayElementModel } from '../../../../../Common/Reports/models/chart-display-element.model';
import { ChartExportEventModel } from '../../../../../Common/Charts/models/chart-export-event.model';
import { ChartBlockComponent } from '../../../../../Common/Reports/components/ChartBlock/chart-block.component';
import { ReportsSeriesPreviewService } from '../../../../../Common/Reports/services/report-series-preview.service';
import { ReportsChartOptionsService } from '../../../../../Common/Reports/services/reports-chart-options.service';
import { printHelper } from '../../../../../Common/Misc/helpers/print/print.helper';
import { download } from '../../../../../Common/Misc/helpers/download/download.helper';
import { chartColors } from '../../../../../Common/Charts/enums/chart-colors.enum';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../../../../Core/Users/services/current-user-state.service';
import { MonitorConfigService } from '../../../../../Common/MonitorShared/services/monitor-config.service';
import { UserPreferencesService } from '../../../../../Account/UserPreferences/services/user-preferences.service';
import { ClickOutsideService } from '../../../../../Common/PageBuilder/services/click-outside.service';
import { ReportsChartDetailService } from '../../../../../Common/Reports/services/reports-chart-detail.service';
import { SocialChartDetailsDialogService } from '@Social/Intelligence/services/social-chart-details-dialog.service';
import { TopTermsListChartDisplayElementModel } from '@Social/Intelligence/models/top-terms-list-chart-display-element.model';
import { TopTermEvent } from '../../../../../Common/Charts/components/TopTermsChart/top-terms-chart.component';
import { ConfigService } from '../../../../../Core/Config/services/config.service';
import { WordCloudChartOptionsModel } from '../../../../../Common/Charts/models/WordCloudChart/word-cloud-chart-options.model';
import { DataConnectionService } from '../../../../../Core/Users/services/data-connection.service';
import { ReportsChartSeriesConverterService } from '../../../../../Common/Reports/services/reports-chart-series-converter.service';
import { ReportsSeriesPreviewNewService } from '../../../../../Common/Reports/services/reports-series-preview-new.service';

@Component({
    selector: 'agpr-top-terms-list-chart-block',
    templateUrl: './top-terms-list-chart-block.component.html',
    styleUrls: ['./top-terms-list-chart-block.component.scss'],
    providers: [
        {
            provide: BlockComponent,
            useExisting: forwardRef(() => TopTermsListChartBlockComponent)
        }
    ],
    standalone: false
})
export class TopTermsListChartBlockComponent extends ChartBlockComponent implements OnChanges, OnInit {
    protected exportClass: string = 'agpr-topTermsChart--exporting';
    public series: SeriesModel[];
    public chartOptions: WordCloudChartOptionsModel = {};
    public sourceId: string = '';
    public initialLabel = '';

    @ViewChild('exportMenu') exportMenu;

    constructor(
        public differs: KeyValueDiffers,
        public pageBuilderService: AbstractPageBuilderService,
        public reportSeriesPreviewService: ReportsSeriesPreviewService,
        public reportsChartOptionsService: ReportsChartOptionsService,
        public elementRef: ElementRef,
        public localizationService: LocalizationService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        public changeDetectorRef: ChangeDetectorRef,
        public userStateService: CurrentUserStateService,
        public monitorConfigService: MonitorConfigService,
        protected userPreferencesService: UserPreferencesService,
        public clickOutsideService: ClickOutsideService,
        public reportsChartDetailService: ReportsChartDetailService,
        public socialChartDetailsDialogService: SocialChartDetailsDialogService,
        public configService: ConfigService,
        public dataConnectionService: DataConnectionService,
        public reportsChartSeriesConverterService: ReportsChartSeriesConverterService,
        public reportsSeriesPreviewNewService: ReportsSeriesPreviewNewService
    ) {
        super(
            differs,
            pageBuilderService,
            reportSeriesPreviewService,
            reportsChartOptionsService,
            localizationService,
            appBaseHref,
            changeDetectorRef,
            userStateService,
            null,
            monitorConfigService,
            userPreferencesService,
            clickOutsideService,
            reportsChartDetailService,
            socialChartDetailsDialogService,
            configService,
            elementRef,
            dataConnectionService,
            reportsChartSeriesConverterService,
            reportsSeriesPreviewNewService
        );
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.initialLabel = (this.block.displayElement as TopTermsListChartDisplayElementModel)?.initialLabel ?? '';
        this.chartOptions = this.reportsChartOptionsService.getTopTermsOptions(this.block.displayElement as ChartDisplayElementModel);
    }

    ngOnChanges(): void {
        this.renderable = this.block.displayElement.canRender();
        this.updateChart();
    }

    selectTerm(event: TopTermEvent) {
        this.outputEvent.emit(event);
    }

    updateChart() {
        this.renderable = this.block.displayElement.canRender();

        this.displayElement = this.block.displayElement as ChartDisplayElementModel;
        if (!this.displayElement || !this.displayElement.series || this.displayElement.series.length < 1) {
            this.series = null;
            return;
        }
        this.series = this.displayElement.series;
    }

    /**
     * Gets the series label from the chart
     */
    public getExportSeriesLabel(): string {
        if (this.series && this.series[0]) {
            return this.series[0].label || '';
        } else {
            return '';
        }
    }

    /**
     * Handles the export button click action, exporting the chart in the selected format
     */
    public async chartExport(event: ChartExportEventModel) {
        // this removes the :hover rule that causes the border of the chart to be blue
        const container: HTMLDivElement = this.elementRef.nativeElement.children[0];
        container.classList.remove('agpr-block');

        this.exportMenu.nativeElement.style.display = 'none';

        const exportType = event.eventKey.toLowerCase() as keyof am4core.IExportOptions;

        let url: string;
        let blob: Blob;

        switch (exportType) {
            case 'jpg':
                url = await htmlToImage.toJpeg(this.elementRef.nativeElement.children[0]);
                break;

            case 'png':
                url = await htmlToImage.toPng(this.elementRef.nativeElement.children[0]);
                break;

            case 'svg':
                url = await htmlToImage.toSvg(this.elementRef.nativeElement.children[0]);
                break;

            case 'csv':
                const csvLabels = [];
                const csvValues = [];
                const csvData = [
                    csvLabels,
                    csvValues
                ];

                const csvString: string = papaparse.unparse(csvData, {delimiter: ','});

                blob = new Blob(['\ufeff', csvString], { type: 'text/csv;charset=utf-8;'});
                break;

            case 'json':
                const jsonData = {};

                blob = new Blob([JSON.stringify(jsonData)], { type: 'text/json;charset=utf-8;'});
                break;

            case 'print':
                const printElement = this.elementRef.nativeElement.children[0];
                const printData = await htmlToImage.toSvg(printElement);
                printHelper.image(printData, document.title, {fit: printElement.offsetHeight <= 1500});
                break;
        }

        if (blob) {
            download.blob(blob, `${this.displayElement.title}.${exportType}`);

        } else if (url) {
            download.url(url, `${this.displayElement.title}.${exportType}`);
        }

        this.exportMenu.nativeElement.style.display = 'block';
        container.classList.add('agpr-block');
    }

    /**
     * Gets the export image for the full report pdf export
     */
    public async getFullExportImage(width: number, height: number): Promise<string> {

        const el: HTMLDivElement = this.elementRef.nativeElement.children[0];
        const classes: DOMTokenList = this.elementRef.nativeElement.children[0].classList;

        el.style.border = 'none';

        // Sets a bunch of styles for scaling to the page size of the pdf
        classes.add(this.exportClass);
        el.style.width = width + 'px';
        el.style.minWidth = width + 'px';
        el.style.height = height + 'px';
        el.style.minHeight = height + 'px';
        el.style.flex = 'unset';

        // Gets the image data URI
        const png = await htmlToImage.toPng(this.elementRef.nativeElement.children[0]);

        // Resets the styles to the original
        el.style.width = '';
        el.style.minWidth = '';
        el.style.height = '';
        el.style.minHeight = '';
        el.style.flex = '';

        el.style.border = `1px solid ${chartColors.agprLightGrey}`;

        classes.remove(this.exportClass);

        return png;
    }
}
